import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, Button, Table, Card, Spinner } from "react-bootstrap";
import { barometreOperations, barometreSelector } from "state/ducks/store/barometre";
import Help from "./Help";

const DARK = "#1d1d1b";

/**
 * Référentiel employés (nom + statut canonique) : sert à pré-remplir le statut
 * des réponses à l'import d'une vague. Le statut d'une vague déjà importée
 * n'est jamais modifié (l'historique par vague fige l'évolution de chacun).
 */
const Employes = ({ onBack }) => {
  const dispatch = useDispatch();
  const employes = useSelector(barometreSelector.getEmployesSelector);

  const [nom, setNom] = useState("");
  const [statutNew, setStatutNew] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [importMsg, setImportMsg] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    dispatch(barometreOperations.getEmployes());
  }, [dispatch]);

  const statuts = employes.statuts || [];

  const handleAdd = () => {
    if (!nom.trim()) return;
    dispatch(barometreOperations.createEmploye(nom.trim(), statutNew || null));
    setNom("");
    setStatutNew("");
  };

  const handleDelete = (e) => {
    if (!window.confirm(`Supprimer « ${e.personne} » du référentiel ?\n(les vagues existantes ne sont pas modifiées)`)) return;
    dispatch(barometreOperations.deleteEmploye(e.id));
  };

  // Collage depuis le Google Sheet : lignes « Nom<TAB>Fonction<TAB>Statut »
  // ou « Nom<TAB>Statut » — la dernière colonne non vide fait foi.
  const handleImport = () => {
    const rows = pasteText
      .split("\n")
      .map((l) => l.split("\t").map((c) => c.trim()))
      .filter((cols) => cols[0])
      .map((cols) => ({
        personne: cols[0],
        statut: cols.length > 1 ? cols[cols.length - 1] || (cols.length > 2 ? cols[2] : "") : "",
      }));
    if (!rows.length) {
      setImportMsg({ ok: false, text: "Rien à importer." });
      return;
    }
    setImporting(true);
    setImportMsg(null);
    Promise.resolve(dispatch(barometreOperations.importEmployes(rows)))
      .then(() => {
        setImporting(false);
        setImportMsg({ ok: true, text: `${rows.length} ligne(s) envoyée(s).` });
        setPasteText("");
        dispatch(barometreOperations.getEmployes());
      })
      .catch(() => {
        setImporting(false);
        setImportMsg({ ok: false, text: "Échec de l'import." });
      });
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h4 style={{ color: DARK, margin: 0 }}>
          Employés &amp; statuts
          <Help title="Référentiel employés" placement="bottom">
            La liste « qui a quel statut » (EP / ES - Adjoint / Respo Coord). À l'import d'une
            vague, le statut de chaque répondant est <b>pré-rempli</b> depuis cette liste s'il
            n'est pas déjà dans le fichier. <b>Les vagues déjà importées ne changent jamais</b> :
            si quelqu'un évolue (ES en 2025 → Respo en 2026), chaque vague garde le statut de
            l'époque — c'est l'historique de son évolution.
          </Help>
        </h4>
        <Button variant="link" onClick={onBack} style={{ paddingRight: 0 }}>
          ← Retour aux vagues
        </Button>
      </div>

      <Card style={{ marginTop: 12 }}>
        <Card.Body>
          <strong>Importer / mettre à jour depuis Google Sheets</strong>
          <Help title="Import collé" placement="right">
            Dans le Google Sheet, sélectionne les lignes (Nom / Fonction / Statut ou juste
            Nom / Statut), copie (Cmd+C) et colle ici. Les noms déjà présents sont mis à jour,
            les nouveaux créés. Ne touche pas aux vagues existantes.
          </Help>
          <Form.Control
            as="textarea"
            rows={4}
            placeholder={"Jean Dupont\tEmployé Polyvalent (EP)\nMarie Martin\tRespo Coord"}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            style={{ fontFamily: "monospace", fontSize: 12, marginTop: 8 }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
            <Button size="sm" onClick={handleImport} disabled={importing || !pasteText.trim()}>
              {importing ? <Spinner animation="border" size="sm" /> : "Importer"}
            </Button>
            {importMsg && (
              <span style={{ color: importMsg.ok ? "#429853" : "#c0392b", fontSize: 13 }}>{importMsg.text}</span>
            )}
          </div>
        </Card.Body>
      </Card>

      <Card style={{ marginTop: 12 }}>
        <Card.Body>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
            <Form.Control
              placeholder="Prénom Nom"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              style={{ maxWidth: 260 }}
            />
            <Form.Control
              as="select"
              value={statutNew}
              onChange={(e) => setStatutNew(e.target.value)}
              style={{ width: "auto" }}
            >
              <option value="">— Statut —</option>
              {statuts.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Form.Control>
            <Button size="sm" onClick={handleAdd} disabled={!nom.trim()}>
              Ajouter
            </Button>
          </div>

          {employes.isLoading ? (
            <Spinner animation="border" />
          ) : (
            <Table size="sm" hover>
              <thead>
                <tr>
                  <th>Personne</th>
                  <th style={{ width: 260 }}>Statut</th>
                  <th style={{ width: 100 }}></th>
                </tr>
              </thead>
              <tbody>
                {(employes.data || []).map((e) => (
                  <tr key={e.id}>
                    <td style={{ verticalAlign: "middle" }}>{e.personne}</td>
                    <td>
                      <Form.Control
                        as="select"
                        size="sm"
                        value={e.statut || ""}
                        onChange={(ev) =>
                          dispatch(barometreOperations.updateEmploye(e.id, { statut: ev.target.value || null }))
                        }
                      >
                        <option value="">—</option>
                        {statuts.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </Form.Control>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(e)}>
                        Supprimer
                      </Button>
                    </td>
                  </tr>
                ))}
                {(!employes.data || employes.data.length === 0) && (
                  <tr>
                    <td colSpan={3} style={{ textAlign: "center", color: "#888" }}>
                      Aucun employé — colle la liste depuis le Google Sheet ci-dessus.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Employes;
